import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Chip, TextField, Grid,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Snackbar, Divider, Card, CardContent,
  InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import { Search, Download, Info, History } from '@mui/icons-material';
import { fetchAuditLog, fetchAuditStats } from '../../api/api';

const ACTION_COLOR = (action = '') => {
  if (action.includes('create')) return 'success';
  if (action.includes('update') || action.includes('edit')) return 'info';
  if (action.includes('delete') || action.includes('cancel') || action.includes('void')) return 'error';
  if (action.includes('send')) return 'primary';
  if (action.includes('open') || action.includes('close')) return 'warning';
  return 'default';
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rowsPerPage] = useState(20);
  const [filters, setFilters] = useState({ actor: '', action: '', resource: '', dateFrom: '', dateTo: '' });
  const [detailRow, setDetailRow] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const load = useCallback(async (pg = page) => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: pg + 1,
        limit: rowsPerPage,
        ...(filters.actor && { actor: filters.actor }),
        ...(filters.action && { action: filters.action }),
        ...(filters.resource && { resource: filters.resource }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
      };
      const res = await fetchAuditLog(params);
      setLogs(res.data?.logs || res.data || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => { load(0); }, [filters]);
  useEffect(() => { load(page); }, [page]);

  useEffect(() => {
    fetchAuditStats().then(r => setStats(r.data?.topActions || r.data || [])).catch(() => {});
  }, []);

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const exportCSV = () => {
    const headers = ['Timestamp', 'Actor', 'Action', 'Resource', 'IP'];
    const rows = logs.map(l => [
      new Date(l.createdAt || l.timestamp).toLocaleString(),
      `${l.actor?.name || ''} (${l.actor?.email || ''})`,
      l.action,
      l.resource,
      l.ipAddress || l.ip || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">Audit Log</Typography>
          <Typography variant="body2" color="text.secondary">Track all admin actions and system events</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Download />} onClick={exportCSV} disabled={logs.length === 0}>
          Export CSV
        </Button>
      </Box>

      {/* Stats */}
      {stats.length > 0 && (
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {stats.slice(0, 5).map((s, i) => (
            <Grid item xs={6} sm={4} md={2.4} key={i}>
              <Card sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="h6" fontWeight={900} color="text.primary">{(s.count || 0).toLocaleString()}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>{s.action || s._id}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Actor" size="small" fullWidth value={filters.actor}
              onChange={e => setFilters(f => ({ ...f, actor: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label="Action" size="small" fullWidth value={filters.action}
              onChange={e => setFilters(f => ({ ...f, action: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField label="Resource" size="small" fullWidth value={filters.resource}
              onChange={e => setFilters(f => ({ ...f, resource: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label="From" type="date" size="small" fullWidth value={filters.dateFrom}
              onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label="To" type="date" size="small" fullWidth value={filters.dateTo}
              onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
        </Grid>
      </Paper>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && (
        <Paper sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Resource</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 5 }}>
                        <History sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">No audit log entries found</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log, i) => (
                    <TableRow key={log._id || i} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailRow(log)}>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                          {log.createdAt || log.timestamp
                            ? new Date(log.createdAt || log.timestamp).toLocaleString()
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{log.actor?.name || log.actorName || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{log.actor?.email || log.actorEmail}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={log.action} size="small" color={ACTION_COLOR(log.action)} sx={{ fontWeight: 700, fontSize: 11 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.resource || log.resourceType}</Typography>
                        {log.resourceId && <Typography variant="caption" color="text.secondary" fontFamily="monospace">{String(log.resourceId).slice(-8)}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">{log.ipAddress || log.ip || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDetailRow(log); }}>
                            <Info fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20]}
            onPageChange={handlePageChange}
          />
        </Paper>
      )}

      {/* Detail Dialog */}
      <Dialog open={Boolean(detailRow)} onClose={() => setDetailRow(null)} maxWidth="md" fullWidth>
        <DialogTitle fontWeight={800}>Audit Entry Details</DialogTitle>
        <DialogContent>
          {detailRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={2}>
                {[
                  { label: 'Action', value: <Chip label={detailRow.action} size="small" color={ACTION_COLOR(detailRow.action)} /> },
                  { label: 'Actor', value: `${detailRow.actor?.name || detailRow.actorName || '-'} (${detailRow.actor?.email || '-'})` },
                  { label: 'Resource', value: detailRow.resource || '-' },
                  { label: 'IP Address', value: detailRow.ipAddress || detailRow.ip || '-' },
                  { label: 'User Agent', value: detailRow.userAgent || '-' },
                  { label: 'Timestamp', value: detailRow.createdAt ? new Date(detailRow.createdAt).toLocaleString() : '-' },
                ].map((item, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</Typography>
                    <Box sx={{ mt: 0.3 }}>
                      {typeof item.value === 'string' ? (
                        <Typography variant="body2" color="text.primary">{item.value}</Typography>
                      ) : item.value}
                    </Box>
                  </Grid>
                ))}
              </Grid>
              {(detailRow.before || detailRow.after) && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" fontWeight={700}>Changes</Typography>
                  <Grid container spacing={2}>
                    {detailRow.before !== undefined && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">BEFORE</Typography>
                        <Box sx={{ mt: 0.5, p: 1.5, bgcolor: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 2, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', overflowX: 'auto', color: 'text.primary' }}>
                          {JSON.stringify(detailRow.before, null, 2)}
                        </Box>
                      </Grid>
                    )}
                    {detailRow.after !== undefined && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">AFTER</Typography>
                        <Box sx={{ mt: 0.5, p: 1.5, bgcolor: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 2, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', overflowX: 'auto', color: 'text.primary' }}>
                          {JSON.stringify(detailRow.after, null, 2)}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
              {detailRow.details && !detailRow.before && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" fontWeight={700}>Details</Typography>
                  <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', overflowX: 'auto', color: 'text.primary' }}>
                    {JSON.stringify(detailRow.details, null, 2)}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailRow(null)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
